import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { cn, setCookie } from "@/lib/utils";
import { getLeadEmailFieldError, isLeadEmailFieldValid } from "@/lib/validation";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/Select";
import { ArrowUpRight } from "lucide-react";
import OtpScreen from "@/components/molecules/OtpScreen";
import { PhoneNoInput } from "@/components/atoms/PhoneNumber";
import { setSessionStorage } from "@/sessionStorage";
import { ILeadDropPopup } from "@/api/admin/leadDropPopup";
import {
  LeadEmailValidationError,
  useCreateLead,
  buildMobileOtpLeadPayload,
  type FormValues,
} from "@/hooks/useCreateLead";
import { updateLeadProgramme } from "@/api/lead";
import { LEAD_DROP_STEPS, USER_STATUS } from "@/constants/lead";
import LeadDrop7ThankYou from "./LeadDrop7ThankYou";
import { RootState } from "@/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsLeadSubmittedSuccessfully,
  setLeadDetailsAction,
} from "@/store/slices/globalslice";
import type { IQualification } from "@/store/slices/leaddrop";
import { authSuccess, leadSubmitted, minimalEdit, modalView } from "@/customAnalytics/trackingEvents";
import { pushToGTMHashed } from "@/utils/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { ICourse } from "@/api/admin/course";
import {
  mergeLeadPopup7Dynamics,
  parseDynamicFieldDefinitions,
  validateDynamicFields,
  type LeadPopup7DynamicField,
} from "./leadPopup7DynamicFields";
import {
  isUserVerified as isUserVerifiedFromCookie,
  setFormValuesInCookie,
  setUserData,
  getStoredApplicationRedirectUrl,
  getCachedLeadCourseInterestKeys,
  normalizeLeadCourseInterestKey,
  getUserData,
} from "@/lib/userDataCookie";

const OMS_TOKEN_COOKIE_MAX_AGE_SEC = 131400 * 60;

/**
 * Checkpoint keys for the post-OTP "complete your profile" step. The modal
 * unmounts on close and wipes the in-component OTP digits + `isExistingLeadId`;
 * persisting them lets the user reopen and finish registering.
 */
const PENDING_OTP_KEY = "ld7_pending_otp";
const PENDING_LEAD_ID_KEY = "ld7_pending_lead_id";

function clearPendingProfileCheckpoint() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(PENDING_OTP_KEY);
  sessionStorage.removeItem(PENDING_LEAD_ID_KEY);
}

const rawAppRedirectSec = Number(
  process.env.NEXT_PUBLIC_APPLICATION_REDIRECT_SECONDS,
);
const APPLICATION_REDIRECT_COUNTDOWN_SEC =
  Number.isFinite(rawAppRedirectSec) && rawAppRedirectSec > 0
    ? rawAppRedirectSec
    : 10;

/** Shown when OTP fails validation; API often returns "Invalid OTP" which we normalize. */
const OTP_MISMATCH_DISPLAY = "Please enter the correct OTP.";

function mapInvalidOtpApiMessage(apiMessage?: string | null): string {
  const s = String(apiMessage ?? "").trim();
  if (!s) return OTP_MISMATCH_DISPLAY;
  if (/invalid[\s._-]*otp/i.test(s)) return OTP_MISMATCH_DISPLAY;
  return s;
}

function isLeadFlowVerifiedPersisted(): boolean {
  if (typeof window === "undefined") return false;
  return (
    isUserVerifiedFromCookie() ||
    sessionStorage.getItem("isUserVerified") === "true"
  );
}

/** Session backup when cookie `leads_id` is missing. */
function readLeadPkFromSessionDetails(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem("u_details");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { leadId?: unknown; id?: unknown };
    const v = parsed.leadId ?? parsed.id;
    if (v == null) return null;
    const s = String(v).trim();
    return s || null;
  } catch {
    return null;
  }
}

/** Target course key for comparing cached submission vs this page/widget (benefits / enrollments). */
function resolveVerifiedRoutingCourseKey(
  lockEnrollment: boolean,
  enrollmentBound: string | null | undefined,
  courseErp: ICourse,
  formCourse: string,
  formInterest: string,
  coursesList: ICourse[],
): string {
  if (lockEnrollment && enrollmentBound != null && String(enrollmentBound).trim()) {
    return normalizeLeadCourseInterestKey(String(enrollmentBound), coursesList);
  }
  const fromForm = normalizeLeadCourseInterestKey(
    String(formCourse || formInterest || "").trim(),
    coursesList,
  );
  if (fromForm) return fromForm;
  const erpRaw =
    courseErp && typeof courseErp === "object" && courseErp.erp_unicode
      ? String(courseErp.erp_unicode ?? "").trim()
      : "";
  return normalizeLeadCourseInterestKey(erpRaw, coursesList);
}

/** IIT production modal close control */
function LeadDropModalCloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12.3971 12.5538L12.4697 12.4697C12.7359 12.2034 13.1526 12.1792 13.4462 12.3971L13.5303 12.4697L20 18.939L26.4697 12.4697C26.7626 12.1768 27.2374 12.1768 27.5303 12.4697C27.8232 12.7626 27.8232 13.2374 27.5303 13.5303L21.061 20L27.5303 26.4697C27.7966 26.7359 27.8208 27.1526 27.6029 27.4462L27.5303 27.5303C27.2641 27.7966 26.8474 27.8208 26.5538 27.6029L26.4697 27.5303L20 21.061L13.5303 27.5303C13.2374 27.8232 12.7626 27.8232 12.4697 27.5303C12.1768 27.2374 12.1768 26.7626 12.4697 26.4697L18.939 20L12.4697 13.5303C12.2034 13.2641 12.1792 12.8474 12.3971 12.5538L12.4697 12.4697L12.3971 12.5538Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface Leaddrop7Props {
  onClose?: () => void;
  data?:
    | {
        formData?: ILeadDropPopup | null;
        courses?: any[];
        defaultValues?: any;
      }
    | ILeadDropPopup
    | null;
  defaultValues?: any;
  variant?: string;
  formValues: any;
  setFormValues: React.Dispatch<React.SetStateAction<any>>;
  step: string;
  setStep: React.Dispatch<React.SetStateAction<string>>;
  showFields: boolean;
  setShowFields: React.Dispatch<React.SetStateAction<boolean>>;
  termsAccepted: boolean;
  setTermsAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Post-OTP "complete your profile" mode. Owned by the parent widget state so
   * it survives the modal closing (DialogContent unmounts Leaddrop7); falls
   * back to local state when a caller doesn't lift it.
   */
  pendingRegistrationAfterOtp?: boolean;
  setPendingRegistrationAfterOtp?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  open?: boolean;
  /** Inline in page (e.g. Program Enrollments card): no hero art, no modal chrome. */
  embedded?: boolean;
  /** Program Enrollments: course locked to CMS-bound university course (erp unicode or id string). */
  enrollmentBoundCourseSelectValue?: string | null;
  /** When true, course select is disabled and kept in sync with the enrollment-bound value. */
  lockEnrollmentCourse?: boolean;
}

interface FormErrors {
  mobileNumber?: string;
  terms?: string;
  email?: string;
}

/**
 * After OTP, API returns `redirectUrl` (often `/` when env unset). Assigning `/`
 * reloads the marketing site, skips thank-you (LAST_SCREEN), and resets modal state.
 * Only hard-navigate when URL is a real handoff (external LMS or non-root same-origin path).
 */
function shouldHardNavigateToPostLoginRedirect(redirectUrl: string): boolean {
  if (typeof window === "undefined") return false;
  const raw = redirectUrl.trim();
  if (!raw || raw === "#") return false;
  try {
    const u = new URL(raw, window.location.origin);
    const isSameOriginRoot =
      u.origin === window.location.origin &&
      (u.pathname === "/" || u.pathname === "") &&
      (!u.search || u.search === "");
    if (isSameOriginRoot) return false;
    return true;
  } catch {
    return /^https?:\/\//i.test(raw);
  }
}

/** IIT OTP desktop title: coerce default CMS black/no token to Figma accent. */
function resolveIitLeadOtpTitleColor(raw?: string | null): string {
  const t = String(raw ?? "").trim().toLowerCase().replace(/\s/g, "");
  const blackTokens = new Set([
    "",
    "#000",
    "#000000",
    "black",
    "rgb(0,0,0)",
    "rgba(0,0,0,1)",
  ]);
  if (blackTokens.has(t)) return "#DB5803";
  const orig = String(raw ?? "").trim();
  return orig || "#DB5803";
}

const Leaddrop7 = ({
  onClose,
  data,
  defaultValues: _defaultValues,
  variant: _variant,
  formValues,
  setFormValues,
  step,
  setStep,
  showFields,
  setShowFields,
  termsAccepted,
  setTermsAccepted,
  pendingRegistrationAfterOtp: pendingRegistrationAfterOtpProp,
  setPendingRegistrationAfterOtp: setPendingRegistrationAfterOtpProp,
  open,
  embedded = false,
  enrollmentBoundCourseSelectValue = null,
  lockEnrollmentCourse = false,
}: Leaddrop7Props) => {
  const dispatch = useDispatch();
  const {
    brochure_description,
    brochure_description_color,
    brochure_desktop_image,
    brochure_mobile_image,
    brochure_title,
    brochure_link_color,
    brochure_title_color,
    brochure_image_alt,
    button_color,
    button_text_color,
    desktop_bg_image,
    mobile_bg_image,
    bg_image_alt,
    existing_user_description,
    existing_user_description_color,
    existing_user_desktop_image,
    existing_user_mobile_image,
    existing_user_image_alt,
    existing_user_title,
    existing_user_title_color,
    form_sub_text,
    form_sub_text_color,
    form_title,
    form_title_color,
    new_user_description,
    new_user_description_color,
    new_user_desktop_image,
    new_user_mobile_image,
    new_user_image_alt,
    new_user_title,
    new_user_title_color,
    signup_button_text,
    signup_icon,
    otp_title_text,
    otp_title_color,
    edit_text_color,
    resend_text_color,
    dynamic_field_definitions,
    get_otp_button_text,
    verify_otp_button_text,
    get_otp_enabled_color,
    phone_row_background_color,
  } = ((data && typeof data === "object" && "formData" in data
    ? data.formData
    : data) || {}) as ILeadDropPopup;

  /** Default orange on modal; embedded enrollment card lavender → use darker hue for ≥4.5:1 AA on phone step. */
  const embeddedFormTitleAccessibleColor = "#6E3418";
  const titleColor = embedded
    ? String(form_title_color ?? "").trim() || embeddedFormTitleAccessibleColor
    : String(form_title_color ?? "").trim() || "#DE5803";

  const heroDesktopSrc = desktop_bg_image || mobile_bg_image || "";
  const heroMobileStripSrc = mobile_bg_image || desktop_bg_image || "";

  const isMobile = useIsMobile(800);

  const {
    courses,
    qualification,
    variant: reduxVariant,
    downloadLink,
  } = useSelector((state: RootState) => state.leaddrop);

  // Always use Redux variant as the source of truth (it's updated when handleOpenLeaddrop is dispatched)
  // The prop variant might be stale or default to REGISTER
  // Redux state persists the variant correctly through the entire flow
  const currentVariant = reduxVariant;

  const courseErpData = useSelector(
    (state: RootState) => state.globalslice.course,
  );

  const universitySettings = useSelector(
    (state: RootState) => state.globalslice.university,
  );
  const isPreview = useSelector((state: RootState) => state.leaddrop.isPreview);
  const dynamicFieldDefs = useMemo(
    () => parseDynamicFieldDefinitions(dynamic_field_definitions ?? []),
    [dynamic_field_definitions],
  );

  const figmaLeadStep1 =
    !embedded && step === LEAD_DROP_STEPS.MOBILE_NUMBER;

  const figmaIitOtpStep =
    !embedded && step === LEAD_DROP_STEPS.OTP_VERIFICATION;

  /** IIT modal: mobile shell + footer chrome for phone or OTP (not thank-you). */
  const figmaIitLeadModalChrome =
    !embedded &&
    (step === LEAD_DROP_STEPS.MOBILE_NUMBER ||
      step === LEAD_DROP_STEPS.OTP_VERIFICATION);
  /** Same left hero column as phone/OTP on desktop (dialog is w-fit — without this, thank-you collapses narrow). */
  const showLead7DesktopHeroColumn =
    !embedded &&
    (figmaLeadStep1 ||
      figmaIitOtpStep ||
      step === LEAD_DROP_STEPS.LAST_SCREEN);
  /**
   * IIT Figma thank-you mobile (node 255:13387): rounded shell + inner stack; must not use generic hero strip.
   */
  const figmaIitThankYouMobile =
    !embedded &&
    step === LEAD_DROP_STEPS.LAST_SCREEN &&
    Boolean(heroDesktopSrc || heroMobileStripSrc);
  const registerFormFieldsActive = showFields;

  const buildMergedLeadFormValues = (): FormValues & {
    c2?: string;
    c3?: string;
    c4?: string;
    c5?: string;
  } =>
    mergeLeadPopup7Dynamics(
      formValues,
      formValues.dynamic || {},
      dynamicFieldDefs,
    );

  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [otpError, setOtpError] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const userStatus = useRef(USER_STATUS.NEW);

  const {
    submitAfterOtpFlow,
    sendOtpMobileFlow,
    isExistingLeadId,
    isExistingLead,
    setIsExistingLead,
    setIsExistingLeadId,
    leadsId,
    setLeadsId,
  } = useCreateLead();

  const [localPendingRegistrationAfterOtp, setLocalPendingRegistrationAfterOtp] =
    useState(false);
  const pendingRegistrationAfterOtp =
    pendingRegistrationAfterOtpProp ?? localPendingRegistrationAfterOtp;
  const setPendingRegistrationAfterOtp =
    setPendingRegistrationAfterOtpProp ?? setLocalPendingRegistrationAfterOtp;

  const [verifiedLeadProgrammeSwitchMode, setVerifiedLeadProgrammeSwitchMode] =
    useState(false);
  const [postSubmitApplicationUrl, setPostSubmitApplicationUrl] = useState(
    () =>
      typeof window !== "undefined" && isLeadFlowVerifiedPersisted()
        ? getStoredApplicationRedirectUrl()
        : "",
  );

  useEffect(() => {
    if (typeof window === "undefined" || open !== true) return;
    if (!isLeadFlowVerifiedPersisted()) return;
    const url = getStoredApplicationRedirectUrl();
    if (url) setPostSubmitApplicationUrl(url);
  }, [open, step]);

  useEffect(() => {
    if (!isExistingLead) {
      userStatus.current = USER_STATUS.NEW;
    }
  }, [isExistingLead]);

  useEffect(() => {
    if (!open) {
      setVerifiedLeadProgrammeSwitchMode(false);
    }
  }, [open]);

  // Track modal views — fire a `modal_view` event whenever the visible screen
  // changes while the modal is open. Leaddrop7 has no separate OTHER_DETAILS
  // step: the register/details screen is MOBILE_NUMBER with the extra fields
  // shown (registerFormFieldsActive), so map that to "details_screen".
  useEffect(() => {
    if (open !== true) return;
    modalView({
      section: "lead_drop",
      component: "lead_submit_modal",
      label: "Enroll Now",
      step:
        step === LEAD_DROP_STEPS.OTP_VERIFICATION
          ? "otp_details_screen"
          : step === LEAD_DROP_STEPS.LAST_SCREEN
            ? "success_screen"
            : registerFormFieldsActive
              ? "details_screen"
              : "mobile_screen",
    });
  }, [open, step, registerFormFieldsActive]);

  /**
   * The post-OTP "complete your profile" mode is parent-owned so it survives
   * the modal closing, but the OTP digits and `isExistingLeadId` it needs to
   * finalize live inside this component and are wiped when DialogContent
   * unmounts on close. Without restoring them, reopening leaves the register
   * form unfinishable: "Register Now" calls submitAfterOtpFlow with an empty
   * OTP/lead id and throws before ever hitting the API. We checkpoint both to
   * sessionStorage when entering the profile step (see PENDING_OTP_KEYS usage)
   * and restore them here on reopen so the user can resume and submit.
   */
  useEffect(() => {
    if (open !== true || typeof window === "undefined") return;
    if (!pendingRegistrationAfterOtp) return;
    if (verifiedLeadProgrammeSwitchMode) return;
    if (String(isExistingLeadId || "").trim()) return;
    const savedLeadId = sessionStorage.getItem(PENDING_LEAD_ID_KEY);
    const savedOtp = sessionStorage.getItem(PENDING_OTP_KEY);
    if (savedLeadId && savedLeadId.trim()) {
      setIsExistingLeadId(savedLeadId.trim());
    }
    if (savedOtp && /^\d{4}$/.test(savedOtp)) {
      setOtp(savedOtp.split(""));
    }
  }, [
    open,
    pendingRegistrationAfterOtp,
    verifiedLeadProgrammeSwitchMode,
    isExistingLeadId,
    setIsExistingLeadId,
  ]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [dynamicErrors, setDynamicErrors] = useState<Record<string, string>>({});

  const validateMobileNumber = (value: string): string | undefined => {
    const v = (value ?? "").trim();
    if (!v) {
      return "Mobile number is required";
    }
    if (!/^\d+$/.test(v)) {
      return "Mobile number must contain only numbers";
    }
    if (v.length !== 10) {
      return "Enter a valid 10-digit mobile number";
    }
    if (!/^[6-9]/.test(v)) {
      return "Enter Valid Phone Number";
    }
    return undefined;
  };

  const patchDynamicValue = (field: LeadPopup7DynamicField, raw: string) => {
    const val = typeof raw === "string" ? raw : String(raw ?? "");
    setFormValues((prev: Record<string, any>) => {
      const prevDyn =
        typeof prev.dynamic === "object" && prev.dynamic !== null
          ? (prev.dynamic as Record<string, string>)
          : {};
      const nextDyn = { ...prevDyn, [field.id]: val };
      const patch: Record<string, unknown> = { dynamic: nextDyn };
      if (field.kind === "qualification" || field.bind === "qualification") {
        patch.qualification = val;
      }
      if (field.kind === "course" || field.bind === "course") {
        patch.course = val;
      }
      if (field.bind === "name") patch.name = val;
      if (field.bind === "email") patch.email = val;
      if (field.bind === "interest") patch.interest = val;
      if (field.bind === "first_name" || field.bind === "last_name") {
        let fn = "";
        let ln = "";
        for (const f of dynamicFieldDefs) {
          const cell =
            f.id === field.id ? val : String(nextDyn[f.id] ?? "").trim();
          if (f.bind === "first_name") fn = String(cell).trim();
          if (f.bind === "last_name") ln = String(cell).trim();
        }
        const combined = [fn, ln].filter(Boolean).join(" ").trim();
        if (combined) patch.name = combined;
      }
      return { ...prev, ...patch };
    });
    minimalEdit({
      section: "lead_drop",
      component: "dynamic_field",
      step: registerFormFieldsActive ? "2" : "1",
      entity: field.id,
      label: `${field.kind}_edit`,
    });
    if (dynamicErrors[field.id]) {
      setDynamicErrors((prev) => {
        const copy = { ...prev };
        delete copy[field.id];
        return copy;
      });
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormValues({ ...formValues, phone: value });

    if (errors.mobileNumber) {
      const err = validateMobileNumber(value);
      if (!err) {
        setErrors({ ...errors, mobileNumber: undefined });
      }
    }
  };

  const handleMobileBlur = () => {
    const error = validateMobileNumber(formValues.phone);
    if (error) {
      setErrors({ ...errors, mobileNumber: error });
    } else {
      setErrors({ ...errors, mobileNumber: undefined });
    }

    if (formValues.phone.length > 0) {
      minimalEdit({
        section: "lead_drop",
        component: "mobile_number_input",
        step: "1",
        entity: "mobile_number",
        label: "mobile_number_edit",
      });
    }
  };

  const readDynamicCell = (fid: string) =>
    String(((formValues.dynamic || {}) as Record<string, string>)[fid] ?? "");

  /**
   * Inline email validation on blur, mirroring the lead form widgets
   * (e.g. LeaddropForm). Optional empty fields stay silent; required empty
   * fields surface the required message; non-empty values run the shared
   * lead email check so users get feedback before submit.
   */
  const handleDynamicEmailBlur = (field: LeadPopup7DynamicField) => {
    const isEmail = field.kind === "email" || field.bind === "email";
    if (!isEmail) return;
    const v = readDynamicCell(field.id).trim();
    if (!v) {
      if (field.required) {
        setDynamicErrors((prev) => ({
          ...prev,
          [field.id]: "This field is required",
        }));
      }
      return;
    }
    const err = getLeadEmailFieldError(v);
    setDynamicErrors((prev) => {
      const copy = { ...prev };
      if (err) copy[field.id] = err;
      else delete copy[field.id];
      return copy;
    });
  };

  const renderDynamicFieldRows = (opts?: {
    figmaMobile?: boolean;
    fields?: LeadPopup7DynamicField[];
  }) => {
    const figmaMobile = opts?.figmaMobile ?? false;
    const fields = opts?.fields ?? dynamicFieldDefs;

    /** IIT lead modal popup — Register now fields: match phone-row control (lavender, 12px, px-3 py-1). */
    const iitPopupRegisterChrome =
      figmaMobile && !embedded && figmaIitLeadModalChrome;
    const registerPopupFieldBg =
      phone_row_background_color &&
      String(phone_row_background_color).trim()
        ? phone_row_background_color
        : "#FFFFFF";

    const iitPopupFieldBgStyle = (
      err: boolean,
    ): React.CSSProperties | undefined =>
      iitPopupRegisterChrome
        ? { backgroundColor: err ? "#FFF3F4" : registerPopupFieldBg }
        : undefined;

    const figmaInput = cn(
      "flex h-9 w-full min-w-0 rounded-md border-none bg-white px-[10px] py-[8px]",
      "text-[14px] font-normal leading-[138%] text-[#464646] shadow-sm",
      "transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium",
      "placeholder:text-[#848484] placeholder:font-normal",
      "focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
      "outline-none ring-0 focus:ring-0 focus-visible:ring-0",
    );
    const figmaInputIitPopupRegister = cn(
      "flex h-9 w-full min-w-0 rounded-md border-none px-3 py-1",
      "text-xs font-normal leading-4 text-[#464646] shadow-sm",
      "transition-[color,background-color,border-color,text-decoration-color,fill,stroke] duration-150",
      "ease-[cubic-bezier(0.4,0,0.2,1)]",
      "file:border-0 file:bg-transparent file:text-xs file:font-medium",
      "placeholder:text-[#848484] placeholder:font-normal",
      "focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
      "outline-none ring-0 focus:ring-0 focus-visible:ring-0",
      "[-webkit-tap-highlight-color:transparent]",
    );
    const figmaSelectTrigger = cn(
      figmaInput,
      "flex items-center justify-between gap-2 whitespace-nowrap pr-3",
      "[&_svg]:h-5 [&_svg]:w-5 [&_svg]:shrink-0 [&_svg]:text-[#848484]",
      "[&_[data-placeholder]]:text-[#848484]",
    );
    const figmaSelectTriggerIitPopupRegister = cn(
      figmaInputIitPopupRegister,
      "flex items-center justify-between gap-2 min-h-0 whitespace-nowrap pr-3",
      "[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 [&_svg]:text-[#848484]",
      "[&_[data-placeholder]]:text-[#848484]",
    );

    return fields.map((field) => {
      const err = dynamicErrors[field.id];
      const controlKey = `${field.id}-${field.kind}`;
      const labelWrap =
        field.label != null && !figmaMobile ? (
          <span className="mb-1 block text-left text-[11px] font-medium leading-tight text-[#393939]">
            {field.label}
            {field.required ? (
              <span className="ml-0.5 text-red-600">*</span>
            ) : null}
          </span>
        ) : null;

      if (field.kind === "qualification") {
        const valRaw =
          readDynamicCell(field.id) || (formValues.qualification ?? "");
        const qualOpts = (qualification || []).filter((option: IQualification) =>
          String(
            (option as IQualification & { value?: string }).value ??
              option.id ??
              "",
          ).trim() !== "",
        );
        const val =
          valRaw &&
          qualOpts.some(
            (o: IQualification) =>
              String(
                (o as IQualification & { value?: string }).value ?? o.id ?? "",
              ) === valRaw,
          )
            ? valRaw
            : undefined;
        return (
          <div key={controlKey} className="flex min-w-0 flex-col gap-[6px]">
            {labelWrap}
            <Select
              modal={false}
              value={val}
              onValueChange={(value: string) => patchDynamicValue(field, value)}
              disabled={qualOpts.length === 0}
            >
              <SelectTrigger
                style={iitPopupFieldBgStyle(!!err)}
                className={cn(
                  figmaMobile
                    ? iitPopupRegisterChrome
                      ? cn(figmaSelectTriggerIitPopupRegister, {
                          "border border-[#F74958]": err,
                          "opacity-50 cursor-not-allowed": qualOpts.length === 0,
                        })
                      : cn(figmaSelectTrigger, {
                          "border border-[#F74958] bg-[#FFF3F4]": err,
                          "opacity-50 cursor-not-allowed": qualOpts.length === 0,
                        })
                    : cn(
                        "box-border flex w-full min-w-0 items-center justify-between rounded-[3px] border px-4 py-3",
                        "focus:outline-none focus:ring-2",
                        "sm:h-[32px] sm:rounded-[4px] sm:text-heading-10-regular",
                        "md:h-[36px] md:rounded-[4px] md:text-heading-9-regular md:px-4",
                        "lg:h-[36px]",
                        "xl:h-[36px]",
                        {
                          "border-[#F74958]": err,
                          "border-gray-300": !err,
                          "opacity-50 cursor-not-allowed": qualOpts.length === 0,
                        },
                      ),
                )}
              >
                <SelectValue
                  placeholder={
                    figmaMobile
                      ? field.placeholder || "Select your Highest Qualification"
                      : field.placeholder || "Select your qualification"
                  }
                />
              </SelectTrigger>
              {qualOpts.length > 0 && (
                <SelectContent
                  className={cn(
                    "z-[200] bg-white shadow-lg",
                    "max-h-[min(280px,var(--radix-select-content-available-height))]",
                    "w-[var(--radix-select-trigger-width)]",
                    figmaMobile
                      ? "rounded-[10px] p-1 shadow-[0px_4px_2px_rgba(0,0,0,0.25)]"
                      : "rounded-lg",
                  )}
                  position="popper"
                  sideOffset={4}
                >
                  {qualOpts.map((option: IQualification) => {
                    const qv = String(
                      (option as IQualification & { value?: string }).value ??
                        option.id ??
                        "",
                    );
                    const qLabel =
                      option.name ?? option.program_name ?? qv;
                    return (
                      <SelectItem
                        key={qv}
                        value={qv}
                        className={cn(
                          "w-full cursor-pointer px-3 py-2",
                          figmaMobile &&
                            "rounded-[6px] py-2.5 text-[14px] font-normal leading-[138%] text-[#464646]",
                        )}
                      >
                        {qLabel}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              )}
            </Select>
            {err && (
              <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
                {err}
              </p>
            )}
          </div>
        );
      }

      if (field.kind === "course") {
        const valRaw = readDynamicCell(field.id) || (formValues.course ?? "");
        const courseOpts = (courses || [])
          .map((option: ICourse) => {
            const erp = String(option.erp_unicode ?? "").trim();
            const selectValue =
              erp || (option.id != null ? String(option.id) : "");
            return { ...option, selectValue };
          })
          .filter((o) => o.selectValue !== "");
        const val =
          valRaw &&
          courseOpts.some((o) => o.selectValue === valRaw)
            ? valRaw
            : undefined;
        return (
          <div key={controlKey} className="flex min-w-0 flex-col gap-[6px]">
            {labelWrap}
            <Select
              modal={false}
              value={val}
              onValueChange={(value: string) => patchDynamicValue(field, value)}
              disabled={courseOpts.length === 0 || lockEnrollmentCourse}
            >
              <SelectTrigger
                style={iitPopupFieldBgStyle(!!err)}
                className={cn(
                  figmaMobile
                    ? iitPopupRegisterChrome
                      ? cn(figmaSelectTriggerIitPopupRegister, {
                          "border border-[#F74958]": err,
                          "opacity-50 cursor-not-allowed":
                            courseOpts.length === 0,
                        })
                      : cn(figmaSelectTrigger, {
                          "border border-[#F74958] bg-[#FFF3F4]": err,
                          "opacity-50 cursor-not-allowed":
                            courseOpts.length === 0,
                        })
                    : cn(
                        "box-border flex w-full min-w-0 items-center justify-between rounded-[3px] border px-4 py-3",
                        "focus:outline-none focus:ring-2",
                        "sm:h-[32px] sm:rounded-[4px] sm:text-heading-10-regular",
                        "md:h-[36px] md:rounded-[4px] md:text-heading-9-regular md:px-4",
                        "lg:h-[36px]",
                        "xl:h-[36px]",
                        {
                          "border-[#F74958] bg-[#FFF3F4]": err,
                          "border-gray-300": !err,
                          "opacity-50 cursor-not-allowed":
                            courseOpts.length === 0,
                        },
                      ),
                )}
              >
                <SelectValue
                  placeholder={
                    field.placeholder || "Select your course"
                  }
                />
              </SelectTrigger>
              {courseOpts.length > 0 && (
                <SelectContent
                  className={cn(
                    "z-[200] bg-white shadow-lg",
                    "max-h-[min(280px,var(--radix-select-content-available-height))]",
                    "w-[var(--radix-select-trigger-width)]",
                    figmaMobile
                      ? "rounded-[10px] p-1 shadow-[0px_4px_2px_rgba(0,0,0,0.25)]"
                      : "rounded-lg",
                  )}
                  position="popper"
                  sideOffset={4}
                >
                  {courseOpts.map((option) => (
                    <SelectItem
                      key={option.selectValue}
                      value={option.selectValue}
                      className={cn(
                        "w-full cursor-pointer px-3 py-2",
                        figmaMobile &&
                          "rounded-[6px] py-2.5 text-[14px] font-normal leading-[138%] text-[#464646]",
                      )}
                    >
                      {option.program_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              )}
            </Select>
            {err && (
              <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
                {err}
              </p>
            )}
          </div>
        );
      }

      if (field.kind === "select") {
        const opts = field.options ?? [];
        const valRaw = readDynamicCell(field.id);
        const val =
          valRaw && opts.some((o) => o.value === valRaw)
            ? valRaw
            : undefined;
        return (
          <div key={controlKey} className="flex min-w-0 flex-col gap-[6px]">
            {labelWrap}
            <Select
              modal={false}
              value={val}
              onValueChange={(value: string) => patchDynamicValue(field, value)}
              disabled={opts.length === 0}
            >
              <SelectTrigger
                style={iitPopupFieldBgStyle(!!err)}
                className={cn(
                  figmaMobile
                    ? iitPopupRegisterChrome
                      ? cn(figmaSelectTriggerIitPopupRegister, {
                          "border border-[#F74958]": err,
                        })
                      : cn(figmaSelectTrigger, {
                          "border border-[#F74958] bg-[#FFF3F4]": err,
                        })
                    : cn(
                        "box-border flex w-full min-w-0 items-center justify-between rounded-[3px] border px-4 py-3",
                        "focus:outline-none focus:ring-2",
                        "sm:h-[32px] sm:rounded-[4px] sm:text-heading-10-regular",
                        "md:h-[36px] md:rounded-[4px] md:text-heading-9-regular md:px-4",
                        {
                          "border-[#F74958]": err,
                          "border-gray-300": !err,
                        },
                      ),
                )}
              >
                <SelectValue
                  placeholder={field.placeholder || "Select"}
                />
              </SelectTrigger>
              {opts.length > 0 && (
                <SelectContent
                  className={cn(
                    "z-[200] bg-white shadow-lg",
                    "max-h-[min(280px,var(--radix-select-content-available-height))]",
                    "w-[var(--radix-select-trigger-width)]",
                    figmaMobile
                      ? "rounded-[10px] p-1 shadow-[0px_4px_2px_rgba(0,0,0,0.25)]"
                      : "rounded-lg",
                  )}
                  position="popper"
                  sideOffset={4}
                >
                  {opts.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      className={cn(
                        "w-full cursor-pointer px-3 py-2",
                        figmaMobile &&
                          "rounded-[6px] py-2.5 text-[14px] font-normal leading-[138%] text-[#464646]",
                      )}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              )}
            </Select>
            {err && (
              <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
                {err}
              </p>
            )}
          </div>
        );
      }

      const v = readDynamicCell(field.id);
      return (
        <div key={controlKey} className="flex min-w-0 flex-col gap-[6px]">
          {labelWrap}
          <Input
            type={field.kind === "email" ? "email" : "text"}
            placeholder={field.placeholder ?? ""}
            value={v}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const isNameField =
                field.bind === "first_name" || field.bind === "last_name";
              // First/last name must contain only letters and spaces — strip
              // digits and special characters (covers typing + paste).
              const next = isNameField
                ? e.target.value.replace(/[^a-zA-Z\s]/g, "")
                : e.target.value;
              patchDynamicValue(field, next);
            }}
            onBlur={() => handleDynamicEmailBlur(field)}
            style={iitPopupFieldBgStyle(!!err)}
            className={cn(
              figmaMobile
                ? iitPopupRegisterChrome
                  ? cn(figmaInputIitPopupRegister, {
                      "border border-[#F74958]": err,
                    })
                  : cn(figmaInput, {
                      "border border-[#F74958] bg-[#FFF3F4]": err,
                    })
                : cn(
                    "box-border w-full min-w-0 rounded-[3px] border px-4 py-3",
                    "focus:outline-none focus:ring-2",
                    "sm:h-[32px] sm:rounded-[4px] sm:text-heading-10-regular",
                    "md:h-[36px] md:rounded-[4px] md:text-heading-9-regular md:px-4",
                    "lg:h-[36px]",
                    "xl:h-[36px]",
                    {
                      "border-[#F74958] bg-[#FFF3F4]": err,
                      "border-gray-300": !err,
                    },
                  ),
            )}
          />
          {err && (
            <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
              {err}
            </p>
          )}
        </div>
      );
    });
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};
    let ok = true;

    const mobileError = validateMobileNumber(formValues.phone);
    if (mobileError) {
      newErrors.mobileNumber = mobileError;
      ok = false;
    }

    if (!registerFormFieldsActive) {
      setErrors(newErrors);
      return ok;
    }

    const dynMap = (formValues.dynamic || {}) as Record<string, string>;
    const dErr = validateDynamicFields(dynamicFieldDefs, dynMap);
    setDynamicErrors(dErr);

    const mergedLead = buildMergedLeadFormValues();
    const mailErr = getLeadEmailFieldError(mergedLead.email || "");
    if (mailErr) {
      newErrors.email = mailErr;
      ok = false;
    }

    if (!termsAccepted) {
      newErrors.terms = "Please accept the terms and conditions";
      ok = false;
    }

    if (Object.keys(dErr).length > 0) {
      ok = false;
    }

    setErrors(newErrors);
    return ok;
  };

  const isFormValid = (): boolean => {
    if (!termsAccepted) return false;

    if (step === LEAD_DROP_STEPS.OTP_VERIFICATION) {
      return (
        otp.length === 4 &&
        otp.every((d) => /^\d$/.test(String(d ?? "").trim()))
      );
    }

    if (!/^\d{10}$/.test((formValues.phone || "").trim())) {
      return false;
    }

    if (!registerFormFieldsActive) {
      return validateMobileNumber(formValues.phone) === undefined;
    }

    const dynMap = (formValues.dynamic || {}) as Record<string, string>;
    const dErr = validateDynamicFields(dynamicFieldDefs, dynMap);
    if (Object.keys(dErr).length > 0) return false;

    const mergedLead = buildMergedLeadFormValues();
    if (!isLeadEmailFieldValid(mergedLead.email || "")) return false;

    return validateMobileNumber(formValues.phone) === undefined;
  };

  const storeFormData = () => {
    const mergedLead = buildMergedLeadFormValues();
    const dataToStore = {
      mobileNumber: mergedLead.phone,
      name: mergedLead.name,
      email: mergedLead.email,
      qualification: mergedLead.qualification,
      course: mergedLead.course,
      dynamic: formValues.dynamic,
      termsAccepted: termsAccepted,
    };
    setSessionStorage("leaddrop-form-data", JSON.stringify(dataToStore));
    const persistAppUrl = getStoredApplicationRedirectUrl();
    setFormValuesInCookie({
      name: mergedLead.name,
      email: mergedLead.email,
      phone: mergedLead.phone,
      qualification: mergedLead.qualification,
      interest: mergedLead.interest || mergedLead.course,
      course: mergedLead.course || mergedLead.interest,
      ...(persistAppUrl ? { applicationRedirectUrl: persistAppUrl } : {}),
      ...(formValues.dynamic &&
      typeof formValues.dynamic === "object" &&
      Object.keys(formValues.dynamic).length > 0
        ? { dynamic: formValues.dynamic }
        : {}),
    });
  };

  const applyFinalizeSuccess = (
    response: {
      success: true;
      data: Record<string, unknown>;
    },
    finalizeExtras?: {
      authLabel?: string;
      authIsFirstByUser?: boolean;
    },
  ) => {
    const d = response.data as {
      token?: string | null;
      cookieKey?: string | null;
      leadData?: unknown;
      redirectUrl?: string;
      applicationRedirectUrl?: string;
    };
    const appRedirect =
      typeof d.applicationRedirectUrl === "string"
        ? d.applicationRedirectUrl.trim()
        : "";
    setPostSubmitApplicationUrl(appRedirect);
    if (typeof document !== "undefined" && d.token && d.cookieKey) {
      setCookie({
        name: d.cookieKey,
        value: d.token,
        maxAge: OMS_TOKEN_COOKIE_MAX_AGE_SEC,
      });
    }
    if (d.leadData && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("u_details", JSON.stringify(d.leadData));
    }

    dispatch(setLeadDetailsAction({ isExistingLead: true }));
    dispatch(setIsLeadSubmittedSuccessfully(true));
    sessionStorage.setItem("isUserVerified", "true");

    const mf = buildMergedLeadFormValues();
    type ApiLeadData = {
      name?: string;
      email?: string;
      mobile?: string;
      highest_qualification?: string;
      area_of_interest?: string;
      leadId?: number | string;
    };
    const ld =
      d.leadData && typeof d.leadData === "object"
        ? (d.leadData as ApiLeadData)
        : undefined;

    const phone = String(ld?.mobile ?? mf.phone ?? "").trim();
    const interest = String(
      ld?.area_of_interest ?? mf.interest ?? mf.course ?? "",
    ).trim();

    setFormValues((prev: Record<string, unknown>) => ({
      ...prev,
      phone,
      name: (ld?.name ?? mf.name ?? prev.name) as string,
      email: (ld?.email ?? mf.email ?? prev.email) as string,
      qualification: (ld?.highest_qualification ??
        mf.qualification ??
        prev.qualification) as string,
      interest: (interest || prev.interest) as string,
      course: (interest || mf.course || prev.course) as string,
    }));

    const fv: Record<string, unknown> = {
      name: String(ld?.name ?? mf.name ?? ""),
      email: String(ld?.email ?? mf.email ?? ""),
      phone,
      qualification: String(ld?.highest_qualification ?? mf.qualification ?? ""),
      interest,
      course: String(mf.course || interest),
    };
    if (
      formValues.dynamic &&
      typeof formValues.dynamic === "object" &&
      Object.keys(formValues.dynamic as object).length > 0
    ) {
      fv.dynamic = formValues.dynamic;
    }
    if (appRedirect) {
      fv.applicationRedirectUrl = appRedirect;
    }

    setUserData({
      isVerified: true,
      leads_id: ld?.leadId != null ? String(ld.leadId) : undefined,
      area_of_interest: interest || undefined,
      interest: interest || undefined,
      ...(appRedirect ? { applicationRedirectUrl: appRedirect } : {}),
      formValues: fv,
    });

    if (appRedirect && typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.setItem("leadApplicationRedirectUrl", appRedirect);
      } catch {
        /* ignore */
      }
    }

    if (ld?.leadId != null && String(ld.leadId).trim() !== "") {
      setLeadsId(String(ld.leadId));
    }
    const areaInterest = interest || mf.interest || mf.course || "";
    pushToGTMHashed(ld?.email ?? mf.email, phone, "lead_drop_form");
    authSuccess({
      section: "",
      component: "submit_button",
      label: finalizeExtras?.authLabel ?? "submit",
      userInfo: {
        university: process.env.NEXT_PUBLIC_UNIVERSITY_NAME,
        mobile: phone,
        name: ld?.name ?? mf.name,
        email: ld?.email ?? mf.email,
        highest_qualification: ld?.highest_qualification ?? mf.qualification,
        area_of_interest: areaInterest,
      },
      additionalInfo: {
        university: process.env.NEXT_PUBLIC_UNIVERSITY_NAME,
        mobile: phone,
        name: ld?.name ?? mf.name,
        email: ld?.email ?? mf.email,
        highest_qualification: ld?.highest_qualification ?? mf.qualification,
        area_of_interest: areaInterest,
      },
      isFirstByUser: finalizeExtras?.authIsFirstByUser ?? true,
    });
    // Fire the `lead_submitted` event once the submission response comes back
    // (verified === true). This is what records the Google Ads conversion for
    // lead-drop popup 7, mirroring the Freshchat email-submission path.
    leadSubmitted({
      section: "lead_drop",
      component: "submit_button",
      label: finalizeExtras?.authLabel ?? "submit",
      capturedInfo: {
        university: process.env.NEXT_PUBLIC_UNIVERSITY_NAME,
        mobile: phone,
        name: ld?.name ?? mf.name,
        email: ld?.email ?? mf.email,
        highest_qualification: ld?.highest_qualification ?? mf.qualification,
        area_of_interest: areaInterest,
      },
      isFirstByUser: finalizeExtras?.authIsFirstByUser ?? true,
      parentEntity: "lead_submit_modal",
    });
    setOtpError("");
    setPendingRegistrationAfterOtp(false);
    setShowFields(false);
    clearPendingProfileCheckpoint();
    userStatus.current = USER_STATUS.EXISTING;

    if (
      typeof window !== "undefined" &&
      d.redirectUrl &&
      typeof d.redirectUrl === "string" &&
      d.token &&
      !appRedirect &&
      shouldHardNavigateToPostLoginRedirect(d.redirectUrl)
    ) {
      window.location.assign(d.redirectUrl);
      return;
    }
    setStep(LEAD_DROP_STEPS.LAST_SCREEN);
  };

  const handleApplyNow = async () => {
    if (step === LEAD_DROP_STEPS.MOBILE_NUMBER) {
      if (pendingRegistrationAfterOtp && registerFormFieldsActive) {
        if (!validateStep1()) return;
        setLoading(true);
        setOtpError("");
        try {
          if (verifiedLeadProgrammeSwitchMode) {
            const leadPk = String(isExistingLeadId || "").trim();
            if (!leadPk) {
              setOtpError("Missing profile. Please refresh and try again.");
              return;
            }

            const mf = buildMergedLeadFormValues() as FormValues & {
              media?: string;
              campaign?: string;
            };
            const regPayload = buildMobileOtpLeadPayload(mf, universitySettings);
            const raw = await updateLeadProgramme(
              leadPk,
              regPayload,
              isPreview,
            );

            if (
              raw &&
              typeof raw === "object" &&
              "success" in raw &&
              raw.success &&
              "data" in raw &&
              raw.data &&
              typeof raw.data === "object" &&
              "verified" in raw.data &&
              (raw.data as Record<string, unknown>).verified === true &&
              (raw.data as Record<string, unknown>).isExistingUser === true
            ) {
              applyFinalizeSuccess(
                raw as { success: true; data: Record<string, unknown> },
                {
                  authLabel: "program_switch_update",
                  authIsFirstByUser: false,
                },
              );
              storeFormData();
              const interestRaw = String(
                mf.course || mf.interest || "",
              ).trim();
              const courseOpts = (courses ?? []) as ICourse[];
              const matchedProgram =
                courseOpts.find(
                  (c) =>
                    String(c.erp_unicode ?? "").trim() === interestRaw ||
                    String(c.id ?? "") === interestRaw,
                ) ?? null;
              const programLabelName = matchedProgram?.program_name?.trim();
              if (programLabelName) {
                setUserData({
                  program_name: programLabelName,
                  submittedInterestName: programLabelName,
                  isVerified: true,
                });
              }
              setVerifiedLeadProgrammeSwitchMode(false);
              return;
            }

            const errMsg =
              raw &&
              typeof raw === "object" &&
              "error" in raw &&
              raw.error &&
              typeof (raw.error as { message?: string }).message === "string"
                ? String((raw.error as { message: string }).message)
                : (raw as { message?: string } | null | undefined)?.message;
            setOtpError(
              errMsg?.trim()
                ? errMsg.trim()
                : "Could not update your details. Please try again.",
            );
            return;
          }

          const response = await submitAfterOtpFlow(
            buildMergedLeadFormValues() as FormValues,
            otp.join(""),
            { includeRegistrationData: true },
          );

          if (response && "needsProfileCompletion" in response && response.needsProfileCompletion) {
            setOtpError("Please fill all required fields.");
            return;
          }

          if (
            response &&
            typeof response === "object" &&
            "success" in response &&
            response.success &&
            "data" in response &&
            response.data &&
            typeof response.data === "object" &&
            "verified" in response.data &&
            response.data.verified === true &&
            (response.data as { needsProfileCompletion?: boolean })
              .needsProfileCompletion !== true
          ) {
            applyFinalizeSuccess(response as { success: true; data: Record<string, unknown> });
            storeFormData();
            return;
          }

          if (
            response &&
            typeof response === "object" &&
            "success" in response &&
            response.success &&
            response.data &&
            typeof response.data === "object" &&
            "verified" in response.data &&
            response.data.verified === false
          ) {
            setOtpError(
              mapInvalidOtpApiMessage(
                (response.data as { message?: string }).message,
              ),
            );
            return;
          }

          setOtpError("Something went wrong. Please try again.");
        } catch (err: unknown) {
          console.error("Error finalizing lead:", err);
          if (err instanceof LeadEmailValidationError) {
            setErrors((prev) => ({ ...prev, email: err.message }));
            setOtpError("");
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      if (registerFormFieldsActive) {
        if (validateStep1()) {
          setLoading(true);
          try {
            await sendOtpMobileFlow(
              String((buildMergedLeadFormValues() as FormValues).phone || "").trim(),
            );
            storeFormData();
            userStatus.current = USER_STATUS.NEW;
            setStep(LEAD_DROP_STEPS.OTP_VERIFICATION);
          } catch (err: unknown) {
            console.error("Error processing OTP:", err);
            if (err instanceof LeadEmailValidationError) {
              setErrors((prev) => ({ ...prev, email: err.message }));
            }
          } finally {
            setLoading(false);
          }
        }
      } else {
        const mobileError = validateMobileNumber(formValues.phone);
        if (mobileError) {
          setErrors({ mobileNumber: mobileError });
          return;
        }
        setLoading(true);
        try {
          await sendOtpMobileFlow(
            String(formValues.phone || "").trim(),
          );
          storeFormData();
          userStatus.current = USER_STATUS.NEW;
          setIsExistingLead(false);
          setStep(LEAD_DROP_STEPS.OTP_VERIFICATION);
        } catch (err: unknown) {
          console.error("Error sending OTP:", err);
        } finally {
          setLoading(false);
        }
      }
    } else if (step === LEAD_DROP_STEPS.OTP_VERIFICATION) {
      setLoading(true);
      setOtpError("");
      try {
        const response = await submitAfterOtpFlow(
          buildMergedLeadFormValues() as FormValues,
          otp.join(""),
          { includeRegistrationData: registerFormFieldsActive },
        );

        if (response && "needsProfileCompletion" in response && response.needsProfileCompletion) {
          // Checkpoint the verified OTP + lead id so the profile step can be
          // resumed and submitted if the user closes and reopens the modal.
          if (typeof window !== "undefined") {
            sessionStorage.setItem(PENDING_OTP_KEY, otp.join(""));
            sessionStorage.setItem(
              PENDING_LEAD_ID_KEY,
              String(isExistingLeadId || "").trim(),
            );
          }
          setPendingRegistrationAfterOtp(true);
          setShowFields(true);
          setStep(LEAD_DROP_STEPS.MOBILE_NUMBER);
          setOtpError("");
          userStatus.current = USER_STATUS.NEW;
          return;
        }

        if (
          response &&
          typeof response === "object" &&
          "success" in response &&
          response.success &&
          response.data &&
          typeof response.data === "object" &&
          "verified" in response.data &&
          response.data.verified === true &&
          (response.data as { needsProfileCompletion?: boolean })
            .needsProfileCompletion !== true
        ) {
          applyFinalizeSuccess(response as { success: true; data: Record<string, unknown> });
          storeFormData();
          return;
        }

        if (
          response &&
          typeof response === "object" &&
          "success" in response &&
          response.success &&
          response.data &&
          typeof response.data === "object" &&
          "verified" in response.data &&
          response.data.verified === false
        ) {
          setOtpError(
            mapInvalidOtpApiMessage(
              (response.data as { message?: string }).message,
            ),
          );
          return;
        }

        setOtpError(OTP_MISMATCH_DISPLAY);
      } catch (error: unknown) {
        console.error("Error verifying OTP:", error);
        if (error instanceof LeadEmailValidationError) {
          setErrors((prev) => ({ ...prev, email: error.message }));
          setOtpError("");
          return;
        }
        const err = error as {
          message?: string;
          response?: { data?: { message?: string } };
        };
        setOtpError(
          mapInvalidOtpApiMessage(
            err?.message || err?.response?.data?.message,
          ),
        );
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open !== true) return;

    const resolveSubmittedCourseValue = (rawInput: string): string | null => {
      const raw = rawInput.trim();
      if (!raw) return null;
      if (!courses?.length) return raw;
      const match = courses.find(
        (c: ICourse) =>
          String(c.erp_unicode ?? "").trim() === raw ||
          String(c.id ?? "") === raw,
      );
      if (!match) return raw;
      const erp = String(match.erp_unicode ?? "").trim();
      return erp || String(match.id ?? "");
    };

    const applyCourseToLeadForm = (rawSelectValue: string) => {
      const resolved = resolveSubmittedCourseValue(rawSelectValue);
      if (!resolved) return;
      const courseFieldId = dynamicFieldDefs.find((f) => f.kind === "course")
        ?.id;
      setFormValues((prev: Record<string, unknown>) => {
        const prevDyn =
          typeof prev.dynamic === "object" &&
          prev.dynamic !== null &&
          !Array.isArray(prev.dynamic)
            ? { ...(prev.dynamic as Record<string, string>) }
            : {};
        if (courseFieldId) {
          prevDyn[courseFieldId] = resolved;
        }
        return {
          ...prev,
          course: resolved,
          interest: resolved,
          ...(courseFieldId ? { dynamic: prevDyn } : {}),
        };
      });
    };

    if (lockEnrollmentCourse && enrollmentBoundCourseSelectValue) {
      applyCourseToLeadForm(String(enrollmentBoundCourseSelectValue));
      return;
    }

    const erpFromPage =
      courseErpData &&
      typeof courseErpData === "object" &&
      "erp_unicode" in courseErpData &&
      courseErpData.erp_unicode
        ? String((courseErpData as ICourse).erp_unicode ?? "").trim()
        : "";

    if (!erpFromPage) return;

    const selectedCourse = courses.find(
      (course) => course.erp_unicode === erpFromPage,
    );

    if (selectedCourse && selectedCourse.erp_unicode) {
      applyCourseToLeadForm(String(selectedCourse.erp_unicode));
    }
  }, [
    open,
    lockEnrollmentCourse,
    enrollmentBoundCourseSelectValue,
    courses,
    courseErpData,
    dynamicFieldDefs,
    setFormValues,
  ]);

  useEffect(() => {
    if (open !== true || typeof window === "undefined") return;
    if (!isLeadFlowVerifiedPersisted()) {
      setVerifiedLeadProgrammeSwitchMode(false);
      return;
    }

    const courseList = (courses ?? []) as ICourse[];
    const targetKey = resolveVerifiedRoutingCourseKey(
      Boolean(lockEnrollmentCourse),
      enrollmentBoundCourseSelectValue,
      (courseErpData || {}) as ICourse,
      String(formValues.course ?? ""),
      String(formValues.interest ?? ""),
      courseList,
    );

    const cachedKeys = getCachedLeadCourseInterestKeys(courseList);

    const leadPkDbg =
      String(getUserData()?.leads_id ?? "").trim() ||
      readLeadPkFromSessionDetails();
    // TEMP DEBUG — remove after diagnosing unmatched-course routing
    console.log("[LD7-route]", {
      lockEnrollmentCourse,
      enrollmentBoundCourseSelectValue,
      formCourse: formValues.course,
      formInterest: formValues.interest,
      coursesLen: courseList.length,
      targetKey,
      cachedKeys,
      matched: targetKey ? cachedKeys.includes(targetKey) : null,
      leadPk: leadPkDbg,
      switchMode: verifiedLeadProgrammeSwitchMode,
    });

    if (verifiedLeadProgrammeSwitchMode) {
      if (targetKey && cachedKeys.includes(targetKey)) {
        setVerifiedLeadProgrammeSwitchMode(false);
        setPendingRegistrationAfterOtp(false);
        setShowFields(false);
        setStep(LEAD_DROP_STEPS.LAST_SCREEN);
      }
      return;
    }

    if (!targetKey) {
      setVerifiedLeadProgrammeSwitchMode(false);
      setStep(LEAD_DROP_STEPS.LAST_SCREEN);
      return;
    }

    if (cachedKeys.length === 0) {
      setVerifiedLeadProgrammeSwitchMode(false);
      setStep(LEAD_DROP_STEPS.LAST_SCREEN);
      return;
    }

    if (cachedKeys.includes(targetKey)) {
      setVerifiedLeadProgrammeSwitchMode(false);
      setStep(LEAD_DROP_STEPS.LAST_SCREEN);
      return;
    }

    // Unmatched course on ANY page: re-open the registration form in
    // programme-switch mode so submitting drops the lead in ERP for the new
    // programme (updateLeadProgramme). Previously only Program Enrollments
    // pages did this and other pages skipped straight to thank-you, which
    // silently swallowed the drop for the new programme.
    const leadPk =
      String(getUserData()?.leads_id ?? "").trim() ||
      readLeadPkFromSessionDetails();
    if (!leadPk) {
      setVerifiedLeadProgrammeSwitchMode(false);
      setStep(LEAD_DROP_STEPS.LAST_SCREEN);
      return;
    }

    setVerifiedLeadProgrammeSwitchMode(true);
    setIsExistingLeadId(leadPk);
    setIsExistingLead(true);
    setShowFields(true);
    setPendingRegistrationAfterOtp(true);
    setStep(LEAD_DROP_STEPS.MOBILE_NUMBER);
  }, [
    open,
    lockEnrollmentCourse,
    enrollmentBoundCourseSelectValue,
    courseErpData,
    formValues.course,
    formValues.interest,
    courses,
    verifiedLeadProgrammeSwitchMode,
    setStep,
    setShowFields,
    setIsExistingLeadId,
    setIsExistingLead,
  ]);

  const showLead7PhoneStep =
    step === LEAD_DROP_STEPS.MOBILE_NUMBER && !registerFormFieldsActive;
  const otpVerificationStep =
    step === LEAD_DROP_STEPS.OTP_VERIFICATION;
  /**
   * On the phone-only "GET OTP" step the button stays disabled until 10 digits
   * are entered (and terms consented). Once 10 digits are in, a tap runs the
   * full validation (e.g. the leading 6-9 rule) and surfaces the invalid-number
   * message.
   *
   * The "Register Now" step mirrors this: the button is enabled once the
   * required fields (incl. a non-empty email) are filled, deferring format
   * validation (e.g. a malformed email) to the on-click validateStep1 so the
   * message surfaces on submit rather than silently disabling the button.
   *
   * Other steps (OTP) still require a fully valid form before enabling submit.
   */
  const phoneHasTenDigits = /^\d{10}$/.test((formValues.phone || "").trim());
  const registerNowStep =
    step === LEAD_DROP_STEPS.MOBILE_NUMBER && registerFormFieldsActive;
  const isRegisterStepFilled = (): boolean => {
    if (!termsAccepted || !phoneHasTenDigits) return false;
    const dynMap = (formValues.dynamic || {}) as Record<string, string>;
    for (const d of dynamicFieldDefs) {
      if (d.required && !String(dynMap[d.id] ?? "").trim()) return false;
    }
    const mergedLead = buildMergedLeadFormValues();
    if (!String(mergedLead.email ?? "").trim()) return false;
    return true;
  };
  const primaryCtaActive = showLead7PhoneStep
    ? termsAccepted && phoneHasTenDigits
    : registerNowStep
      ? isRegisterStepFilled()
      : isFormValid();
  const disableSubmit = loading || !primaryCtaActive;

  const enabledOtpTone =
    String(get_otp_enabled_color ?? "").trim() || "#DB5803";
  // Disabled CTA stays a fixed grey (matches iitkgp gray-600); CMS disabled color is intentionally ignored.
  const disabledOtpTone = "#BEBEBE";

  const useOtpToneForPrimaryCta =
    showLead7PhoneStep ||
    (otpVerificationStep && figmaIitLeadModalChrome) ||
    (otpVerificationStep && embedded) ||
    (registerFormFieldsActive && (figmaLeadStep1 || embedded));

  const primaryCtaBackground = useOtpToneForPrimaryCta
    ? primaryCtaActive
      ? enabledOtpTone
      : disabledOtpTone
    : button_color;

  const submitLabel = loading
    ? "Verifying..."
    : otpVerificationStep
      ? verify_otp_button_text?.trim() || "Verify OTP"
      : registerFormFieldsActive
        ? "Register Now"
        : get_otp_button_text || signup_button_text || "GET OTP";

  /** Avoid duplicate IDs when embedded card + popup both mount during navigation. */
  const consentCheckboxId = embedded
    ? "consent_mobile_program_enrollment"
    : "consent_mobile";

  return (
    <section
      className={cn(
        embedded
          ? "relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-transparent"
            : [
              "relative flex w-full flex-col bg-white",
              figmaIitLeadModalChrome || figmaIitThankYouMobile
                ? cn(
                    "max-[767px]:z-[100] max-[767px]:mx-0 max-[767px]:max-h-none max-[767px]:max-w-full max-[1023px]:min-h-[90dvh] max-[767px]:rounded-tr-[42px] max-[767px]:rounded-bl-[42px] max-[767px]:p-0 min-[768px]:rounded-xl min-[768px]:py-[30px] min-[768px]:px-[29px]",
                    figmaIitThankYouMobile &&
                      step === LEAD_DROP_STEPS.LAST_SCREEN
                      ? "max-[767px]:overflow-x-hidden max-[767px]:overflow-y-auto"
                      : "max-[767px]:overflow-hidden",
                  )
                : [
                    "max-w-[calc(100vw-2rem)] py-4 px-4 sm:px-7",
                    "xs:z-100 xs:min-h-[90dvh] xs:max-w-full xs:rounded-none",
                  ],
              "min-[768px]:max-w-[908px] min-[1024px]:min-h-0 min-[768px]:overflow-hidden",
              !figmaIitLeadModalChrome &&
                !figmaIitThankYouMobile &&
                "min-[768px]:px-[29px] min-[768px]:py-[30px]",
              step === LEAD_DROP_STEPS.LAST_SCREEN && "min-[768px]:rounded-xl",
            ],
      )}
    >
      {onClose && !embedded && (
        <button
          type="button"
          onClick={onClose}
          className={cn(
            "absolute right-4 top-4 z-[60] flex cursor-pointer items-center justify-center",
            (figmaIitLeadModalChrome || figmaIitThankYouMobile) &&
              "max-[767px]:right-3 max-[767px]:top-3",
            figmaIitThankYouMobile
              ? "max-[767px]:z-[80] text-black max-[767px]:text-white"
              : "text-black",
            "transition-opacity hover:opacity-70 active:opacity-70",
            "size-10 p-0",
            "outline-none focus:outline-none focus-visible:outline-none",
            "ring-0 ring-offset-0 focus:ring-0 focus-visible:ring-0",
            "[-webkit-tap-highlight-color:transparent]",
          )}
          aria-label="Close"
        >
          <LeadDropModalCloseIcon
            className={cn(
              "size-10 shrink-0",
              figmaIitThankYouMobile
                ? "max-[767px]:text-white min-[768px]:text-black"
                : figmaIitLeadModalChrome
                  ? step === LEAD_DROP_STEPS.LAST_SCREEN
                    ? "text-black"
                    : "text-white min-[768px]:text-black"
                  : "text-black",
            )}
          />
        </button>
      )}
      <div
        className={cn(
          embedded
            ? "flex min-h-0 w-full min-w-0 flex-1 flex-col"
            : cn(
                "flex min-h-0 w-full flex-1 flex-col min-[1024px]:min-h-[565px] min-[1024px]:flex-row min-[1024px]:items-start",
                figmaIitLeadModalChrome
                  ? "gap-0 min-[1024px]:gap-8"
                  : "gap-6 min-[1024px]:gap-8",
              ),
        )}
      >
        {!embedded &&
        figmaIitLeadModalChrome &&
        step !== LEAD_DROP_STEPS.LAST_SCREEN ? (
          <div className="relative shrink-0 overflow-hidden rounded-tr-[42px] min-[768px]:hidden">
            <div className="relative">
              {/* Design asset: iitkgp leaddrop.svg — phone / OTP / register mobile chrome */}
              <img
                src="/svg/leaddrop.svg"
                alt=""
                width={320}
                height={124}
                className="block h-auto w-full min-h-[7.5rem]"
                decoding="async"
              />
              <div className="absolute inset-0 z-[1] mx-auto flex max-w-[340px] flex-col items-center gap-2 px-4 pb-12 pt-14 text-center text-white">
                {step === LEAD_DROP_STEPS.OTP_VERIFICATION ? (
                  <p className="font-poppins text-[24px] font-semibold leading-[1.6]">
                    {otp_title_text?.trim() || "Enter OTP"}
                  </p>
                ) : registerFormFieldsActive &&
                  dynamicFieldDefs.length > 0 ? (
                  <>
                    <p className="font-poppins text-[24px] font-semibold leading-[36px] tracking-[-0.192px]">
                      {form_title?.trim() || "Register Now"}
                    </p>
                    <p className="font-poppins text-[14px] font-normal leading-6 tracking-[-0.0747px] text-white/95">
                      {form_sub_text?.trim() || "Please enter your details"}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-poppins text-[20px] font-medium leading-[1.6]">
                      {form_title?.trim() || "Please Enter Your Phone No."}
                    </p>
                    {/* <p className="font-poppins text-[14px] font-normal leading-[1.6] text-white/95">
                      {form_sub_text?.trim() ||
                        "Limited Seats Available, Enroll Now!"}
                    </p> */}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {!embedded &&
          !figmaIitLeadModalChrome &&
          step !== LEAD_DROP_STEPS.LAST_SCREEN && (
          <div
            className={cn(
              "relative h-[134px] w-full shrink-0 overflow-hidden lg:hidden",
            )}
          >
            {heroMobileStripSrc ? (
              <Image
                src={heroMobileStripSrc}
                alt={bg_image_alt || "Form background"}
                width={640}
                height={268}
                className="h-full w-full object-cover"
                priority
              />
            ) : (
              <div className="h-full w-full bg-[#F5F6FA]" />
            )}
          </div>
        )}

        {!embedded &&
          showLead7DesktopHeroColumn &&
          (heroDesktopSrc ? (
          <div className="relative hidden shrink-0 overflow-hidden rounded-bl-[45px] min-[1024px]:flex min-[1024px]:h-[565px] min-[1024px]:w-[394px] min-[1024px]:items-center min-[1024px]:justify-center">
            <div
              aria-hidden
              className="absolute inset-0 z-0 rounded-bl-[45px] bg-[#cd9986]"
            />
            <Image
              src={heroDesktopSrc}
              alt={bg_image_alt || "lead-drop"}
              width={394}
              height={565}
              priority
              className="relative z-[1] h-[565px] w-[394px] rounded-bl-[45px] object-cover object-bottom"
            />
          </div>
        ) : (
          <div
            className="relative hidden shrink-0 rounded-tl-[47px] rounded-bl-[45px] border border-[#ffd2d0] bg-[#F5F6FA] min-[1024px]:flex min-[1024px]:h-[565px] min-[1024px]:w-[394px]"
            aria-hidden
          />
        ))}

        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col",
            !embedded &&
              "justify-start min-[1024px]:h-[565px] min-[1024px]:justify-start min-[1024px]:self-stretch",
          )}
        >
          <div
            className={cn(
              "flex min-h-0 min-w-0 w-full flex-1 flex-col",
              !embedded &&
                "h-full w-full max-[767px]:max-w-[420px] min-[1024px]:mx-auto min-[1024px]:max-w-[420px]",
            )}
          >
            <div
              className={cn(
                "relative flex min-h-0 w-full flex-1 flex-col overflow-x-hidden",
                embedded
                  ? cn(
                      "overflow-y-auto overflow-x-hidden scrollbar-hide px-0 pb-0 pt-0",
                      step === LEAD_DROP_STEPS.LAST_SCREEN
                        ? "justify-center"
                        : "justify-start gap-5 lg:gap-6",
                    )
                  : cn(
                      "overflow-y-auto",
                      figmaIitLeadModalChrome &&
                        "max-[767px]:px-4 max-[767px]:pb-6 max-[767px]:pt-10 min-[768px]:overflow-hidden min-[768px]:px-0 min-[768px]:pb-8 min-[768px]:pt-6",
              figmaIitThankYouMobile &&
                "max-[767px]:flex max-[767px]:flex-1 max-[767px]:flex-col max-[767px]:overflow-y-auto max-[767px]:px-0 max-[767px]:pb-0 max-[767px]:pt-0 min-[768px]:overflow-hidden min-[768px]:px-0 min-[768px]:pb-0 min-[768px]:pt-6",
                      !figmaIitLeadModalChrome &&
                        !figmaIitThankYouMobile &&
                        "px-[18px] pb-[14px] pt-8 lg:pb-10 lg:pt-8",
                    ),
                !embedded &&
                  step === LEAD_DROP_STEPS.LAST_SCREEN &&
                  !figmaIitThankYouMobile &&
                  "justify-center",
                !embedded &&
                  step !== LEAD_DROP_STEPS.LAST_SCREEN &&
                  !figmaIitLeadModalChrome &&
                  "justify-start gap-6 lg:gap-7",
                !embedded &&
                  step !== LEAD_DROP_STEPS.LAST_SCREEN &&
                  figmaIitLeadModalChrome &&
                  "justify-start gap-0 max-[767px]:flex-1 min-[768px]:flex min-[768px]:flex-1",
                embedded &&
                  step === LEAD_DROP_STEPS.LAST_SCREEN &&
                  "justify-center",
              )}
            >
            {(figmaLeadStep1 ||
              figmaIitOtpStep ||
              (embedded &&
                registerFormFieldsActive &&
                step !== LEAD_DROP_STEPS.LAST_SCREEN)) && (
              <>
                <div className="flex w-full shrink-0 flex-col gap-8 min-[768px]:mx-auto min-[768px]:w-full min-[1024px]:max-w-[420px] min-[768px]:gap-[56px]">
                  <div
                    className={cn(
                      "flex-col gap-2 items-center text-center",
                      embedded && registerFormFieldsActive
                        ? "flex"
                        : "hidden min-[768px]:flex",
                    )}
                  >
                    {otpVerificationStep ? (
                      <div
                        className="font-poppins text-[32px] font-medium leading-[1.4] tracking-tight"
                        style={{
                          color: resolveIitLeadOtpTitleColor(otp_title_color),
                        }}
                      >
                        {otp_title_text?.trim()
                          ? otp_title_text
                          : "Enter OTP"}
                      </div>
                    ) : (
                      <>
                        <div
                          className={cn(
                            "font-poppins text-[22px] leading-[1.4] tracking-tight min-[768px]:text-[28px]",
                            registerFormFieldsActive
                              ? "font-semibold"
                              : "font-medium",
                          )}
                          style={{
                            color:
                              String(form_title_color ?? "").trim() ||
                              "#DB5803",
                          }}
                        >
                          {registerFormFieldsActive
                            ? (() => {
                                const t = form_title?.trim() ?? "";
                                if (!t) return "Register Now";
                                if (
                                  /^please\s+enter\s+your\s+phone\s*no\.?$/i.test(
                                    t,
                                  )
                                )
                                  return "Register Now";
                                return t;
                              })()
                            : form_title?.trim()
                              ? form_title
                              : "Please Enter Your Phone No."}
                        </div>
                        {form_sub_text ? (
                          <div
                            className="text-center text-sm leading-normal md:text-base"
                            style={{
                              color: form_sub_text_color || "#1F1F1F",
                            }}
                          >
                            {form_sub_text}
                          </div>
                        ) : registerFormFieldsActive ? (
                          <div
                            className="text-center text-sm leading-normal text-[#1F1F1F] md:text-base"
                          >
                            Please enter your details
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                  {(figmaLeadStep1 || embedded) &&
                  registerFormFieldsActive &&
                  dynamicFieldDefs.length > 0 ? (
                    <div className="flex w-full min-w-0 flex-col gap-[13px]">
                      {(() => {
                        const firstNameField = dynamicFieldDefs.find(
                          (d) => d.bind === "first_name",
                        );
                        const lastNameField = dynamicFieldDefs.find(
                          (d) => d.bind === "last_name",
                        );
                        const usedIds = new Set(
                          [firstNameField?.id, lastNameField?.id].filter(
                            Boolean,
                          ) as string[],
                        );
                        const restFields = dynamicFieldDefs.filter(
                          (d) => !usedIds.has(d.id),
                        );
                        return (
                          <>
                            <div
                              className={cn(
                                "flex min-w-0 flex-col gap-[6px]",
                                figmaIitLeadModalChrome
                                  ? "w-full"
                                  : "mx-auto w-full max-w-[314px]",
                              )}
                            >
                              <div
                                className={cn(
                                  "min-w-0",
                                  !pendingRegistrationAfterOtp && "rounded-[4px]",
                                )}
                                style={
                                  pendingRegistrationAfterOtp
                                    ? undefined
                                    : {
                                        backgroundColor:
                                          phone_row_background_color &&
                                          String(
                                            phone_row_background_color,
                                          ).trim()
                                            ? phone_row_background_color
                                            : "#FFFFFF",
                                      }
                                }
                              >
                                <PhoneNoInput
                                  desktopFigmaVariant
                                  disabled={pendingRegistrationAfterOtp}
                                  className={cn(
                                    errors.mobileNumber &&
                                      "border border-[#F74958]",
                                  )}
                                  inputClassName={
                                    errors.mobileNumber
                                      ? "bg-[#FFF3F4]"
                                      : undefined
                                  }
                                  value={formValues.phone}
                                  onChange={handleMobileChange}
                                  onBlur={handleMobileBlur}
                                />
                              </div>
                              {errors.mobileNumber && (
                                <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
                                  {errors.mobileNumber}
                                </p>
                              )}
                            </div>
                            {firstNameField && lastNameField ? (
                              <div className="flex w-full gap-[13px]">
                                <div className="min-w-0 flex-1">
                                  {renderDynamicFieldRows({
                                    figmaMobile: true,
                                    fields: [firstNameField],
                                  })}
                                </div>
                                <div className="min-w-0 flex-1">
                                  {renderDynamicFieldRows({
                                    figmaMobile: true,
                                    fields: [lastNameField],
                                  })}
                                </div>
                              </div>
                            ) : null}
                            {restFields.length > 0
                              ? renderDynamicFieldRows({
                                  figmaMobile: true,
                                  fields: restFields,
                                })
                              : null}
                          </>
                        );
                      })()}
                    </div>
                  ) : figmaLeadStep1 ? (
                    <div
                      className={cn(
                        "flex min-w-0 flex-col",
                        figmaIitLeadModalChrome
                          ? "w-full"
                          : "mx-auto w-full max-w-[314px]",
                      )}
                    >
                      <div
                        className="min-w-0 rounded-[4px]"
                        style={{
                          backgroundColor:
                            phone_row_background_color &&
                            String(phone_row_background_color).trim()
                              ? phone_row_background_color
                              : "#EEEBF7",
                        }}
                      >
                        <PhoneNoInput
                          desktopFigmaVariant
                          disabled={pendingRegistrationAfterOtp}
                          className={cn(
                            errors.mobileNumber && "border border-[#F74958]",
                          )}
                          inputClassName={
                            errors.mobileNumber ? "bg-[#FFF3F4]" : undefined
                          }
                          value={formValues.phone}
                          onChange={handleMobileChange}
                          onBlur={handleMobileBlur}
                        />
                      </div>
                      {errors.mobileNumber && (
                        <p className="text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]">
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
                <div
                  aria-hidden
                  className="hidden w-full shrink-0 min-[768px]:block min-[768px]:min-h-0"
                />
              </>
            )}
            {step === LEAD_DROP_STEPS.MOBILE_NUMBER &&
            !figmaLeadStep1 &&
            !(embedded && registerFormFieldsActive) && (
              <div
                className={cn(
                  "flex flex-col",
                  embedded
                    ? "w-full shrink-0 gap-0"
                    : [
                        "w-full items-start gap-5",
                        "sm:gap-5",
                        "md:gap-5",
                        "lg:gap-6",
                      ],
                )}
              >
                <div
                  className={cn(
                    "flex flex-col",
                    embedded
                      ? "mx-2 text-center md:mx-3"
                      : ["w-full text-left", "sm:gap-0 sm:block lg:text-left"],
                    {
                      "xs:absolute xs:top-[4%] xs:left-1/2 xs:transform xs:-translate-x-1/2":
                        isMobile && !embedded,
                    },
                  )}
                >
                  <div
                    className={cn(
                      "text-center font-poppins tracking-tight",
                      embedded
                        ? "text-[20px] font-semibold leading-[1.38]"
                        : "w-full text-left font-medium text-lg leading-normal lg:text-[28px] lg:leading-[36px]",
                    )}
                    style={{
                      color: titleColor,
                    }}
                  >
                    {form_title?.trim()
                      ? form_title
                      : "Please Enter Your Phone No."}
                  </div>
                  {form_sub_text ? (
                    <div
                      className={cn(
                        "xs:text-heading-9-regular",
                        "sm:text-heading-8-regular sm:leading-normal",
                        "md:text-heading-8-regular md:leading-normal",
                      )}
                      style={{
                        color: form_sub_text_color || "#1F1F1F",
                      }}
                    >
                      {form_sub_text}
                    </div>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "flex min-w-0 flex-col",
                    embedded
                      ? "mx-auto mt-6 w-full min-w-0 max-w-[314px] md:mt-8"
                      : ["min-w-0", "xs:gap-3", "sm:gap-[4px]", "md:gap-[4px]", "lg:gap-[4px]"],
                  )}
                >
                  {step === LEAD_DROP_STEPS.MOBILE_NUMBER && (
                    <div
                      className={cn(
                        "flex min-w-0 flex-col gap-[6px]",
                        embedded ? "w-full" : "mx-auto w-full max-w-[314px]",
                      )}
                    >
                      {embedded ? (
                        <PhoneNoInput
                          embedCard
                          className={cn(
                            "min-w-0",
                            errors.mobileNumber && "border-[#F74958]",
                          )}
                          inputClassName={cn({
                            "bg-[#FFF3F4]": errors.mobileNumber,
                          })}
                          value={formValues.phone}
                          onChange={handleMobileChange}
                          onBlur={handleMobileBlur}
                        />
                      ) : (
                        <div
                          className="min-w-0 rounded-[6px] p-px"
                          style={
                            phone_row_background_color
                              ? { backgroundColor: phone_row_background_color }
                              : { backgroundColor: "#FFFFFF" }
                          }
                        >
                          <PhoneNoInput
                            className={cn(
                              "h-[36px] min-h-[36px] max-h-[36px] min-w-0 rounded-[6px]",
                              "border-transparent",
                              {
                                "border-[#F74958] bg-[#FFF3F4]":
                                  errors.mobileNumber,
                              },
                            )}
                            inputClassName={cn(
                              "min-w-0 border-transparent text-[14px] leading-normal sm:text-[14px] md:text-[14px] lg:text-[14px]",
                              {
                                "border-[#F74958] bg-[#FFF3F4]":
                                  errors.mobileNumber,
                              },
                            )}
                            value={formValues.phone}
                            onChange={handleMobileChange}
                            onBlur={handleMobileBlur}
                          />
                        </div>
                      )}
                      {errors.mobileNumber && (
                        <p className="text-[#F74958] text-[10px] font-medium leading-normal tracking-[-0.053px]">
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                  )}

                  {showFields && dynamicFieldDefs.length > 0 && (
                    <>
                      <div className="flex flex-col gap-[10px] min-w-0">
                        {renderDynamicFieldRows()}
                      </div>
                      {errors.email && (
                        <p className="text-[#F74958] text-[10px] font-medium leading-normal tracking-[-0.053px]">
                          {errors.email}
                        </p>
                      )}
                    </>
                  )}

                  {showFields && dynamicFieldDefs.length === 0 && errors.email && (
                    <p className="text-[#F74958] text-[10px] font-medium leading-normal tracking-[-0.053px]">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === LEAD_DROP_STEPS.OTP_VERIFICATION && errors.email && (
              <p
                className={cn(
                  "text-[#F74958] text-[10px] font-medium leading-normal tracking-[-0.053px] mb-2",
                  embedded ? "mx-2 md:mx-3" : "px-0.5",
                )}
              >
                {errors.email}
              </p>
            )}

            {step === LEAD_DROP_STEPS.OTP_VERIFICATION && (
              <div
                className={cn(
                  embedded && "mx-2 w-full min-w-0 md:mx-3",
                  figmaIitOtpStep && "w-full shrink-0 max-[767px]:mt-6 min-[768px]:mt-16",
                )}
              >
                <OtpScreen
                  figmaIitLeadModal={figmaIitOtpStep}
                  embeddedCard={embedded}
                  setStep={(step: typeof LEAD_DROP_STEPS.MOBILE_NUMBER) =>
                    setStep(step)
                  }
                  phoneNumber={formValues.phone}
                  isExistingLeadId={isExistingLeadId}
                  otp={otp}
                  setOtp={(newOtp) => {
                    setOtp(newOtp);
                    setOtpError(""); // Clear error when user starts typing
                  }}
                  error={otpError}
                  otpTitleText={otp_title_text}
                  otpTitleColor={otp_title_color}
                  editTextColor={edit_text_color}
                  resendTextColor={resend_text_color}
                />
              </div>
            )}

            {step === LEAD_DROP_STEPS.LAST_SCREEN && (
              <LeadDrop7ThankYou
                embedded
                figmaModalMobile={figmaIitThankYouMobile}
                variant={currentVariant as LEAD_DROP_VARIANTS}
                onClose={onClose}
                brochureDownloadLink={downloadLink}
                formValues={formValues}
                courses={(courses ?? []) as ICourse[]}
                coursePageErp={courseErpData as ICourse}
                existingUserTitle={existing_user_title || ""}
                existingUserTitleColor={existing_user_title_color || ""}
                existingUserDescription={existing_user_description || ""}
                existingUserDescriptionColor={
                  existing_user_description_color || ""
                }
                brochureTitle={brochure_title || ""}
                brochureTitleColor={brochure_title_color || ""}
                brochureDescription={brochure_description || ""}
                brochureDescriptionColor={brochure_description_color || ""}
                buttonColor={button_color || ""}
                buttonTextColor={button_text_color || ""}
                applicationRedirectUrl={postSubmitApplicationUrl}
                redirectSeconds={
                  postSubmitApplicationUrl
                    ? APPLICATION_REDIRECT_COUNTDOWN_SEC
                    : 0
                }
              />
            )}

            {step !== LEAD_DROP_STEPS.LAST_SCREEN && (
            <div
              className={cn(
                "flex w-full shrink-0 flex-col",
                embedded
                  ? "mt-auto gap-5 border-t border-[#111827]/8 pt-6 lg:gap-6 lg:pt-8"
                  : figmaIitLeadModalChrome
                    ? "mt-auto max-[767px]:gap-[14px] min-[768px]:gap-[11px]"
                    : "gap-[14px]",
              )}
            >
              <div
                className={cn(
                  embedded &&
                    "mx-auto flex w-full max-w-[314px] shrink-0 justify-center px-2 md:px-0",
                )}
              >
                <Button
                  onClick={handleApplyNow}
                  disabled={disableSubmit}
                  className={cn(
                    embedded && [
                      "!h-auto !min-h-0 !max-h-none w-fit max-w-full shrink-0",
                      "rounded-[6px] border-0 px-[18px] py-[12px] [-webkit-tap-highlight-color:transparent]",
                      "gap-[10px] text-[16px] font-semibold leading-[24px] text-[#FFFFFF]",
                      "shadow-sm transition-[box-shadow,color,background-color]",
                      "disabled:shadow-none",
                      otpVerificationStep &&
                        "font-['Poppins',sans-serif] whitespace-nowrap !font-[600]",
                    ],
                    !embedded && [
                      otpVerificationStep && !figmaIitLeadModalChrome
                        ? [
                            "mx-auto !h-auto !min-h-0 !max-h-none w-fit max-w-full shrink-0",
                            "rounded-[6px] border-0 px-[18px] py-[12px] [-webkit-tap-highlight-color:transparent]",
                            "gap-[10px] text-[16px] font-semibold leading-[24px] whitespace-nowrap",
                            "font-['Poppins',sans-serif] text-[#FFFFFF] !font-[600]",
                            "shadow-sm transition-[box-shadow,color,background-color]",
                            "disabled:shadow-none",
                          ]
                        : [
                            "w-full",
                            "sm:text-heading-8-semibold sm:h-[48px] sm:rounded-[4px]",
                            "md:text-heading-8-semibold md:h-[48px] md:rounded-[4px]",
                            "lg:text-heading-8-semibold lg:h-[48px] lg:rounded-[4px]",
                            "xl:text-heading-8-semibold xl:h-[48px] xl:rounded-[4px]",
                            figmaIitLeadModalChrome &&
                              !otpVerificationStep &&
                              "max-[767px]:shadow-[0_20px_40px_rgba(9,30,66,0.12)] min-[768px]:shadow-[0_4px_14px_rgba(0,0,0,0.12)]",
                          ],
                    ],
                    {
                      "opacity-50 cursor-not-allowed":
                        disableSubmit &&
                        !showLead7PhoneStep &&
                        !useOtpToneForPrimaryCta,
                    },
                    (embedded || otpVerificationStep) &&
                      disableSubmit &&
                      (showLead7PhoneStep || otpVerificationStep) &&
                      "cursor-not-allowed",
                  )}
                  style={{
                    backgroundColor: primaryCtaBackground,
                    color: useOtpToneForPrimaryCta
                      ? "#FFFFFF"
                      : button_text_color || "#FFFFFF",
                  }}
                  id="lead-drop-submit-button"
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center",
                      embedded || otpVerificationStep
                        ? "gap-[10px]"
                        : "w-full gap-2",
                      !embedded &&
                        figmaIitLeadModalChrome &&
                        !otpVerificationStep &&
                        "max-[767px]:gap-[25px]",
                    )}
                  >
                    <span>{submitLabel}</span>
                    {(showLead7PhoneStep ||
                      registerFormFieldsActive ||
                      otpVerificationStep) &&
                    !loading ? (
                      signup_icon &&
                      (showLead7PhoneStep ||
                        (figmaLeadStep1 && registerFormFieldsActive)) ? (
                        <Image
                          src={signup_icon}
                          alt=""
                          width={18}
                          height={18}
                          className="inline-block shrink-0"
                        />
                      ) : (
                        <ArrowUpRight
                          className={cn(
                            "shrink-0 text-[#FFFFFF]",
                            otpVerificationStep && "size-5",
                            !otpVerificationStep &&
                              (embedded
                                ? "size-4 lg:size-5"
                                : cn(
                                    "h-[18px] w-[18px]",
                                    figmaIitLeadModalChrome &&
                                      "max-[767px]:h-5 max-[767px]:w-5",
                                  )),
                          )}
                          aria-hidden
                        />
                      )
                    ) : null}
                  </span>
                </Button>
              </div>
                <div
                  className={cn(
                    "flex flex-col gap-[6px]",
                    embedded && "mx-auto min-w-0 w-full max-w-[314px] md:px-0",
                  )}
                >
                <div
                  className={cn(
                    "flex justify-start text-left",
                    figmaIitLeadModalChrome
                      ? "items-center gap-[7px]"
                      : "items-start gap-[7px]",
                  )}
                >
                  <input
                    type="checkbox"
                    id={consentCheckboxId}
                    checked={termsAccepted}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setTermsAccepted(next);
                      minimalEdit({
                        section: "lead_drop",
                        component: "terms_checkbox",
                        step: "1",
                        entity: "terms",
                        label: next ? "terms_accepted" : "terms_unchecked",
                      });
                      if (errors.terms) {
                        setErrors({ ...errors, terms: undefined });
                      }
                    }}
                    className={cn(
                      "mt-0.5 shrink-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-0",
                      "h-4 min-h-4 w-4 min-w-4 rounded-[4px]",
                    )}
                    style={{ accentColor: "#DB5803" }}
                  />
                  <label
                    htmlFor={consentCheckboxId}
                    className="min-w-0 flex-1 cursor-pointer text-left font-poppins text-[10px] leading-snug break-words text-black [overflow-wrap:anywhere]"
                  >
                    By clicking on submit, I agree to the{" "}
                    <a
                      href="/legal-disclaimer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      Legal Disclaimer
                    </a>
                    {" and "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>
                {errors.terms && (
                  <p
                    className={cn(
                      "mt-1 ml-6 text-[10px] text-red-500",
                    )}
                  >
                    {errors.terms}
                  </p>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default Leaddrop7;
